import {
  Avatar,
  Box,
  Button,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { useState, useEffect } from "react";
import api from "../../config/axios";
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';

function NotificationsSection() {
  const [friendRequests, setFriendRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchFriendRequests();
  }, []);

  const fetchFriendRequests = async () => {
    try {
      setLoading(true);
      const currentUser = JSON.parse(localStorage.getItem('user'));
      console.log('Current user data:', {
        id: currentUser?.id,
        _id: currentUser?._id,
        email: currentUser?.email,
        fullData: currentUser
      });
      
      if (!currentUser) {
        setError('Please log in to see notifications');
        return;
      }

      console.log('Fetching friend requests...');
      const response = await api.get('/friends/requests');
      console.log('All friend requests:', response.data);
      
      // Filter only pending requests where current user is the receiver
      const pendingRequests = response.data.filter(request => {
        // Detailed logging of each request
        console.log('Processing request:', {
          requestId: request._id,
          sender: {
            id: request.sender._id,
            name: `${request.sender.firstName} ${request.sender.lastName}`,
            email: request.sender.email
          },
          receiver: {
            id: request.receiver._id,
            name: `${request.receiver.firstName} ${request.receiver.lastName}`,
            email: request.receiver.email
          },
          status: request.status,
          currentUserId: currentUser._id,
          isMatch: request.receiver._id === currentUser._id
        });
        
        // Check if the current user is the receiver and request is pending
        const isReceiver = request.receiver._id === currentUser._id;
        const isPending = request.status === 'pending';
        
        console.log(`Request ${request._id}: isReceiver=${isReceiver}, isPending=${isPending}, will ${isReceiver && isPending ? 'SHOW' : 'HIDE'} this request`);
        return isPending && isReceiver;
      });
      
      console.log('Final filtered requests:', pendingRequests);
      setFriendRequests(pendingRequests);
    } catch (err) {
      console.error('Error in fetchFriendRequests:', err);
      if (err.response) {
        console.error('Server response:', {
          status: err.response.status,
          data: err.response.data,
          headers: err.response.headers
        });
      }
      setError(err.response?.data?.message || 'Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptRequest = async (requestId) => {
    try {
      const response = await api.put(`/friends/accept/${requestId}`);
      console.log('Accept response:', response.data);
      alert('Friend request accepted!');
      fetchFriendRequests(); // Refresh the list
    } catch (err) {
      console.error('Error accepting friend request:', err);
      console.error('Error response:', err.response?.data);
      alert('Failed to accept friend request');
    }
  };

  const handleRejectRequest = async (requestId) => {
    try {
      const response = await api.put(`/friends/reject/${requestId}`);
      console.log('Reject response:', response.data);
      alert('Friend request rejected');
      fetchFriendRequests(); // Refresh the list
    } catch (err) {
      console.error('Error rejecting friend request:', err);
      console.error('Error response:', err.response?.data);
      alert('Failed to reject friend request');
    }
  };

  if (loading) return <div>Loading notifications...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <Stack
      sx={{
        width: "100%",
        minWidth: "64.5%",
        bgcolor: "#ffffff",
        borderRadius: "22px",
        p: "20px",
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: "bold" }}>
        Notifications
      </Typography>
      <Divider
        sx={{
          height: "1px",
          bgcolor: "#EBEBEB",
          mx: "-20px",
          my: "16px",
          border: "none",
        }}
      />
      <Stack id="notifications-container" direction="column" spacing={1.5}>
        {friendRequests.length === 0 ? (
          <Typography>No new notifications</Typography>
        ) : (
          friendRequests.map((request) => (
            <Stack
              key={request._id}
              sx={{
                bgcolor: "#F8F8F8",
                borderRadius: "12px",
                p: "12px",
                px: "14px",
                border: "1px solid #E3E3E3",
              }}
            >
              <ListItem sx={{ p: 0 }}>
                <ListItemAvatar>
                  <Avatar
                    src={request.sender.profileImage || "/images/default-avatar.jpeg"}
                    alt={request.sender.firstName}
                    sx={{ width: 48, height: 48 }}
                  />
                </ListItemAvatar>
                <ListItemText
                  primary={`${request.sender.firstName} ${request.sender.lastName} sent you a friend request`}
                  secondary={`@${request.sender.email.split('@')[0]}`}
                />
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="contained"
                    color="success"
                    size="small"
                    onClick={() => handleAcceptRequest(request._id)}
                    startIcon={<CheckIcon />}
                  >
                    Accept
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    size="small"
                    onClick={() => handleRejectRequest(request._id)}
                    startIcon={<CloseIcon />}
                  >
                    Reject
                  </Button>
                </Stack>
              </ListItem>
            </Stack>
          ))
        )}
      </Stack>
    </Stack>
  );
}

export default NotificationsSection;
